<template>
  <table class="cxm-TableBase">
    <TableHead>
      <slot name="columns">
        <TableCellHead
          v-if="hasCheckbox"
          align="center"
          is-checkbox
        >
          <slot name="select_all" />
        </TableCellHead>
        <TableCellHead
          v-for="(column, i) in columns"
          :key="i"
          :align="column.align"
          :descending="descending"
          :sortable="column.sortable"
          :sort-by="sortBy"
          :field="column.field"
          :z-index="column.zIndex"
          :unpin-head="unpinHead"
          @sort="$emit('sort', $event)"
        >
          <SkeletonTable v-if="loading" />
          <slot
            v-else
            :name="`head-${column.field}`"
            :column="column"
          >
            {{ column.label }}
          </slot>
        </TableCellHead>

        <TableCellHead
          v-if="hasActions"
          is-action
          :unpin-head="unpinHead"
          :unpin-action="unpinAction"
        />
      </slot>
    </TableHead>

    <TableBody>
      <slot name="rows">
        <template v-if="loading">
          <TableRow
            v-for="(item, i) in Array.apply(null, Array(10))"
            :key="i"
          >
            <TableCell
              v-for="(column, key) in columns"
              :key="key"
            >
              <SkeletonTable />
            </TableCell>
          </TableRow>
        </template>
        <template
          v-for="row in rows"
          v-else
        >
          <TableRow
            :key="row[rowKey]"
            :click-row="() => handleClick(row)"
            :class="{'cxm-TableBase-Row': hasClickRow}"
          >
            <TableCell
              v-if="hasCheckbox"
              align="center"
            >
              <slot
                name="checkbox"
                :row="row"
              />
            </TableCell>
            <TableCell
              v-for="(column, key) in columns"
              :key="key"
              :align="column.align"
              :blocked="row[blockedBy]"
              :lockable="column.lockable"
              :wrap="column.wrap"
            >
              <slot
                :name="column.field"
                :row="row"
              >
                {{ row[column.field] }}
              </slot>
            </TableCell>

            <TableCell
              v-if="hasActions"
              is-action
              :unpin-action="unpinAction"
            >
              <slot
                name="actions"
                :row="row"
              />
            </TableCell>
          </TableRow>

          <slot
            name="subtable"
            :row="row"
          />
        </template>
      </slot>
    </TableBody>
  </table>
</template>

<script>
import SkeletonTable from '@/packages/ui/components/Skeleton/SkeletonTable.vue';
import TableBody from './TableBody.vue';
import TableCell from './TableCell.vue';
import TableCellHead from './TableCellHead.vue';
import TableHead from './TableHead.vue';
import TableRow from './TableRow.vue';

export default {
  name: 'TableBase',
  components: {
    TableBody,
    TableCell,
    TableCellHead,
    TableHead,
    TableRow,
    SkeletonTable,
  },
  props: {
    blockedBy: {
      type: String,
      default: '',
    },
    columns: {
      type: Array,
      default: () => [],
    },
    descending: {
      type: Boolean,
      default: false,
    },
    loading: {
      type: Boolean,
      default: false,
    },
    rows: {
      type: Array,
      default: () => [],
    },
    rowKey: {
      type: String,
      default: 'uuid',
    },
    sortBy: {
      type: String,
      default: '',
    },
    unpinAction: {
      type: Boolean,
      default: false,
    },
    unpinHead: {
      type: Boolean,
      default: false,
    },
    clickRow: {
      type: Function,
      default: () => {},
    },
    hasClickRow: {
      type: Boolean,
      default: false,
    },
  },
  computed: {
    hasActions() {
      return this.$scopedSlots.actions && this.loading === false;
    },
    hasCheckbox() {
      return this.$scopedSlots.checkbox && this.loading === false;
    },
  },
  methods: {
    handleClick(row) {
      if (!this.clickRow) {
        return;
      }
      this.clickRow(row);
    },
  },
};
</script>

<style lang="scss" scoped>
.cxm-TableBase {
  border-spacing: 0;
  width: 100%;
}
.cxm-TableBase-Row {
  :hover{
    cursor: pointer;
  }
}
</style>
